import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useBanner } from '../contexts/BannerContext';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import logo from '../assets/logo_White.png';
const Hero = () => {
  const { t, isRTL } = useLanguage();
  const { showBanner } = useBanner();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="hero" 
      className={`relative min-h-screen flex items-center overflow-hidden transition-all duration-300 ${
        showBanner ? 'pt-20' : 'pt-5'
      }`}
    >
      {/* Animated Background - Fixed z-index */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-teal-50 -z-10">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-cyan-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-amber-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left space-y-8 animate-fade-in-up">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold">
              <Sparkles className="w-4 h-4" />
              <span>{t.offers.badge}</span>
            </div>

            {/* Headline */}
            <div className="space-y-4">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="block text-gray-900">{t.hero.title}</span>
                <span className="block bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                  {t.hero.subtitle}
                </span>
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl">
                {t.hero.description}
              </p>
            </div>

            {/* CTA Buttons - Added relative z-index */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start relative z-20">
              <Button
                size="lg"
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-xl hover:shadow-2xl transition-all duration-300 text-lg px-8 py-6 group cursor-pointer"
              >
                {t.hero.cta1}
                <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'} group-hover:${isRTL ? '-translate-x-1' : 'translate-x-1'} transition-transform`} />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollToSection('services')}
                className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 text-lg px-8 py-6 cursor-pointer"
              >
                {t.hero.cta2}
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 relative z-10">
              {t.hero.stats.map((stat, index) => (
                <div
                  key={index}
                  className="text-center lg:text-left p-4 rounded-xl bg-white/50 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative hidden lg:block animate-fade-in-up animation-delay-500 z-10">
            <div className="relative -mt-16">
              {/* Main card */}
              <div className="relative bg-white rounded-3xl shadow-2xl p-8 transform hover:scale-105 transition-transform duration-500">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <img
                        src={logo}
                        alt="Adidoo Logo"
                        className="w-12 h-12"
                      />
                    </div>
                    <div>
                      <div className="font-bold text-2xl text-gray-900">Adidoo</div>
                      <div className="text-gray-500">IT Solutions</div>
                    </div>
                  </div>
                  
                  {/* Services from footer.services.links */}
                  <div className="space-y-3">
                    {t.footer.services.links.map((service, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-teal-50 transition-colors">
                        <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                        <span className="text-gray-700 font-medium">{service}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating elements with RTL support */}
              <div className={`absolute -top-8 w-24 h-24 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl shadow-xl animate-float ${
                isRTL ? '-left-8' : '-right-8'
              }`}></div>
              <div className={`absolute -bottom-10 w-32 h-32 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-full shadow-xl animate-float animation-delay-2000 ${
                isRTL ? '-right-12' : '-left-12'
              }`}></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;